/* eslint-disable react/no-array-index-key */
import { Suspense, lazy, Fragment } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FormLayout from 'src/layouts/FormLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';
import * as Sentry from '@sentry/react';
import OfflineView from './views/pages/OfflineView';
import CaseLayout from './layouts/CaseLayout/CaseLayout';
import DashBoardLayout from './layouts/DashBoardLayout/DashBoardLayout';
import NonCaseLayout from './layouts/NonCaseLayout/NonCaseLayout';
import { RecoveryLayout } from './layouts/RecoveryLayout';
import { caseRoutes } from './routes/caseRoutes';
import { nonCaseRoutes } from './routes/nonCaseRoutes';
import { recoveryRoutes } from './routes/recoveryRoutes';
import { dashboardRoutes } from './routes/dashboardRoutes';
import { loginRoutes } from './routes/loginRoutes';
import DraiFormLayout from './views/case/DRAIView/Forms/FormLayout';
import { useAppSelector } from './store';

const Error = lazy(() => import('src/views/pages/Error404View'));
const PreviewView = lazy(() => import('src/views/transmit/PreviewView'));
const PdfPreviewWindow = lazy(() => import('src/views/PdfPreviewWindow'));

function getFormLayout({ formId }) {
  if (formId === 'medSocial') {
    return DraiFormLayout;
  } else {
    return FormLayout;
  }
}

function RenderRoutes({ route }) {
  const Form = route.form ? getFormLayout({ formId: route.form }) : Fragment;
  const formProps = route.form && {
    formId: route?.form,
    pageName: route?.page,
    permission: route?.permission,
    upgradedESign: route?.upgradedESign,
  };
  const Component = route.component;
  return (
    <>
      <Helmet>
        <title>{route?.page}</title>
      </Helmet>
      <Form {...formProps}>
        <Sentry.ErrorBoundary fallback={<OfflineView />}>
          <Suspense fallback={<LoadingScreen />}>
            <Component permission={route?.permission} />
          </Suspense>
        </Sentry.ErrorBoundary>
      </Form>
    </>
  );
}

function renderRoute(route, i) {
  return (
    <Route
      exact
      key={route.path + i}
      path={route.path}
      element={<RenderRoutes route={route} />}
    />
  );
}

function renderRecoveryRoutes() {
  return recoveryRoutes.map((route, i) => (
    <Route
      exact
      key={route.path + i}
      path={`recovery/${route.path}`}
      element={
        <>
          <Helmet>
            <title>{route.page}</title>
          </Helmet>
          <RecoveryLayout page={route.page}>
            <RenderRoutes route={route} />
          </RecoveryLayout>
        </>
      }
    />
  ));
}

// function renderDashboardRoutes() {
//   return dashboardRoutes.map((route, i) => renderRoute(route, i));
// }

function renderLoginRoutes() {
  return loginRoutes.map((route, i) => renderRoute(route, i));
}

function Routing() {
  function renderCaseRoutes() {
    return caseRoutes().map((route, i) => renderRoute(route, i));
  }

  function renderNonCaseRoutes() {
    return nonCaseRoutes().map((route, i) => renderRoute(route, i));
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route element={<AuthGuard />}>
          <Route element={<NonCaseLayout />}>{renderNonCaseRoutes()}</Route>
          <Route
            path="case/:caseId"
            element={<CaseLayout />}
          >
            {renderCaseRoutes()}
            {renderRecoveryRoutes()}
          </Route>
          {/* <Route element={<DashBoardLayout />}>{renderDashboardRoutes()}</Route> */}
          <Route
            path="/transmit/preview/:caseId"
            element={<CaseLayout transmit />}
          >
            <Route element={<PreviewView />} />
          </Route>
        </Route>
        <Route element={<GuestGuard />}>{renderLoginRoutes()}</Route>
        <Route
          element={
            <div style={{ flex: '1 1 auto' }}>
              <Outlet />
            </div>
          }
        >
          <Route
            path="/pdf-preview"
            element={<PdfPreviewWindow />}
          />
          <Route
            exact
            path="/404"
            element={<Error />}
          />
          <Route
            path="*"
            element={<Error />}
          />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default Routing;
